import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useQuery } from 'react-query';
import axios from 'axios';
import { useLanguage } from '../../contexts/LanguageContext';
import {
  ChartBarIcon,
  DocumentArrowDownIcon,
  CalendarDaysIcon,
  WrenchScrewdriverIcon,
  CurrencyDollarIcon,
  ClockIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  CogIcon,
  PrinterIcon,
  ShareIcon,
  FunnelIcon,
} from '@heroicons/react/24/outline';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
  AreaChart,
  Area,
} from 'recharts';

const EquipmentReports = () => {
  const { t } = useLanguage();
  const [reportType, setReportType] = useState('overview');
  const [dateRange, setDateRange] = useState('this-month');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedStatus, setSelectedStatus] = useState('all');

  // Fetch equipment and maintenance data
  const { data: equipmentData, isLoading } = useQuery('equipment', async () => {
    const response = await axios.get('/equipments');
    return response.data;
  });

  const { data: maintenanceData } = useQuery('maintenance', async () => {
    const response = await axios.get('/equipments/maintenance');
    return response.data;
  });

  const { data: reportsData } = useQuery('equipment-reports', async () => {
    const response = await axios.get('/equipments/reports');
    return response.data;
  });

  const equipment = equipmentData?.equipment || [];
  const maintenance = maintenanceData?.maintenance || [];
  const reports = reportsData?.reports || {};

  // Equipment categories
  const categories = [
    { id: 'cardio', name: 'Cardio', color: '#ef4444' },
    { id: 'strength', name: 'Musculation', color: '#3b82f6' },
    { id: 'functional', name: 'Fonctionnel', color: '#10b981' },
    { id: 'accessories', name: 'Accessoires', color: '#8b5cf6' },
  ];

  // Calculate statistics
  const stats = {
    totalEquipment: equipment.length,
    activeEquipment: equipment.filter(e => e.status === 'available').length,
    maintenanceEquipment: equipment.filter(e => e.status === 'maintenance').length,
    brokenEquipment: equipment.filter(e => e.status === 'out-of-order').length,
    totalMaintenanceCost: maintenance.reduce((sum, m) => sum + (m.cost || 0), 0),
    avgMaintenanceCost: maintenance.length > 0 ? maintenance.reduce((sum, m) => sum + (m.cost || 0), 0) / maintenance.length : 0,
    totalEquipmentValue: equipment.reduce((sum, e) => sum + (e.purchasePrice || 0), 0),
    maintenanceThisMonth: maintenance.filter(m => {
      const date = new Date(m.scheduledDate);
      const now = new Date();
      return date.getMonth() === now.getMonth() && date.getFullYear() === now.getFullYear();
    }).length,
  };

  // Equipment by category data
  const categoryData = categories.map(category => ({
    name: category.name,
    value: equipment.filter(e => e.category === category.id).length,
    color: category.color,
  }));

  // Equipment status data
  const statusData = [
    { name: 'Disponible', value: stats.activeEquipment, color: '#10b981' },
    { name: 'Maintenance', value: stats.maintenanceEquipment, color: '#f59e0b' },
    { name: 'Hors service', value: stats.brokenEquipment, color: '#ef4444' },
  ];

  // Monthly maintenance cost data
  const monthlyMaintenanceData = [
    { month: 'Jan', cost: 25000, tasks: 8 },
    { month: 'Fév', cost: 32000, tasks: 12 },
    { month: 'Mar', cost: 28000, tasks: 9 },
    { month: 'Avr', cost: 35000, tasks: 15 },
    { month: 'Mai', cost: 42000, tasks: 18 },
    { month: 'Juin', cost: 38000, tasks: 14 },
    { month: 'Juil', cost: 45000, tasks: 20 },
    { month: 'Août', cost: 39000, tasks: 16 },
    { month: 'Sep', cost: 33000, tasks: 11 },
    { month: 'Oct', cost: 41000, tasks: 17 },
    { month: 'Nov', cost: 36000, tasks: 13 },
    { month: 'Déc', cost: 29000, tasks: 10 },
  ];

  // Equipment age distribution
  const ageDistributionData = [
    { range: '0-1 an', count: 15, color: '#10b981' },
    { range: '1-2 ans', count: 12, color: '#3b82f6' },
    { range: '2-3 ans', count: 8, color: '#f59e0b' },
    { range: '3-5 ans', count: 5, color: '#ef4444' },
    { range: '5+ ans', count: 3, color: '#6b7280' },
  ];

  // Equipment utilization data
  const utilizationData = [
    { name: 'Tapis de course 1', utilization: 95, category: 'cardio', hours: 8.5 },
    { name: 'Vélo elliptique 2', utilization: 88, category: 'cardio', hours: 7.8 },
    { name: 'Banc de musculation 3', utilization: 92, category: 'strength', hours: 8.1 },
    { name: 'Rack à squat 1', utilization: 85, category: 'strength', hours: 7.5 },
    { name: 'Tapis de course 2', utilization: 78, category: 'cardio', hours: 6.9 },
    { name: 'Presse à cuisses', utilization: 82, category: 'strength', hours: 7.2 },
    { name: 'Rameur 1', utilization: 75, category: 'cardio', hours: 6.5 },
    { name: 'Banc incliné', utilization: 80, category: 'strength', hours: 7.0 },
  ];

  // Top maintenance issues
  const maintenanceIssues = [
    { issue: 'Usure des courroies', count: 12, cost: 85000, category: 'cardio' },
    { issue: 'Calibrage des poids', count: 8, cost: 45000, category: 'strength' },
    { issue: 'Nettoyage profond', count: 15, cost: 22000, category: 'all' },
    { issue: 'Lubrification', count: 10, cost: 15000, category: 'all' },
    { issue: 'Réparation écrans', count: 6, cost: 95000, category: 'cardio' },
    { issue: 'Remplacement câbles', count: 9, cost: 32000, category: 'strength' },
  ];

  // Filter data based on selections
  const filteredEquipment = equipment.filter(item => {
    const matchesCategory = selectedCategory === 'all' || item.category === selectedCategory;
    const matchesStatus = selectedStatus === 'all' || item.status === selectedStatus;
    return matchesCategory && matchesStatus;
  });

  const handleExport = (format) => {
    // This would trigger an API call to generate and download the report
    console.log(`Exporting ${reportType} report in ${format} format for ${dateRange}`);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('fr-DZ', {
      style: 'currency',
      currency: 'DZD',
      minimumFractionDigits: 0,
      currencyDisplay: 'symbol'
    }).format(amount).replace('DZD', 'DA');
  };

  const reportTypes = [
    { id: 'overview', name: 'Vue d\'ensemble', icon: ChartBarIcon },
    { id: 'maintenance', name: 'Maintenance', icon: WrenchScrewdriverIcon },
    { id: 'utilization', name: 'Utilisation', icon: ClockIcon },
    { id: 'financial', name: 'Financier', icon: CurrencyDollarIcon },
    { id: 'trends', name: 'Tendances', icon: ArrowTrendingUpIcon },
  ];

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-6"
    >
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-white">Rapports d'Équipements</h2>
          <p className="text-white/60 mt-1">
            Analyses et statistiques détaillées sur vos équipements
          </p>
        </div>
        <div className="flex items-center space-x-2">
          <button
            onClick={() => handleExport('pdf')}
            className="flex items-center space-x-2 bg-white/10 text-white px-4 py-2 rounded-xl hover:bg-white/20 transition-colors"
          >
            <DocumentArrowDownIcon className="h-5 w-5" />
            <span>PDF</span>
          </button>
          <button
            onClick={() => handleExport('excel')}
            className="flex items-center space-x-2 bg-white/10 text-white px-4 py-2 rounded-xl hover:bg-white/20 transition-colors"
          >
            <DocumentArrowDownIcon className="h-5 w-5" />
            <span>Excel</span>
          </button>
          <button
            onClick={() => window.print()}
            className="flex items-center space-x-2 bg-white/10 text-white px-4 py-2 rounded-xl hover:bg-white/20 transition-colors"
          >
            <PrinterIcon className="h-5 w-5" />
            <span>Imprimer</span>
          </button>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-6 border border-white/20">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-white/60 text-sm">Total Équipements</p>
              <p className="text-2xl font-bold text-white">{stats.totalEquipment}</p>
            </div>
            <div className="bg-blue-500/20 p-3 rounded-xl">
              <CogIcon className="h-6 w-6 text-blue-400" />
            </div>
          </div>
        </div>

        <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-6 border border-white/20">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-white/60 text-sm">Valeur Totale</p>
              <p className="text-2xl font-bold text-white">{formatCurrency(stats.totalEquipmentValue)}</p>
            </div>
            <div className="bg-green-500/20 p-3 rounded-xl">
              <CurrencyDollarIcon className="h-6 w-6 text-green-400" />
            </div>
          </div>
        </div>

        <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-6 border border-white/20">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-white/60 text-sm">Coût Maintenance</p>
              <p className="text-2xl font-bold text-white">{formatCurrency(stats.totalMaintenanceCost)}</p>
            </div>
            <div className="bg-orange-500/20 p-3 rounded-xl">
              <WrenchScrewdriverIcon className="h-6 w-6 text-orange-400" />
            </div>
          </div>
        </div>

        <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-6 border border-white/20">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-white/60 text-sm">Disponibilité</p>
              <p className="text-2xl font-bold text-white">
                {stats.totalEquipment > 0 ? Math.round((stats.activeEquipment / stats.totalEquipment) * 100) : 0}%
              </p>
            </div>
            <div className="bg-purple-500/20 p-3 rounded-xl">
              <CheckCircleIcon className="h-6 w-6 text-purple-400" />
            </div>
          </div>
        </div>
      </div>

      {/* Report Type Selector */}
      <div className="flex flex-wrap gap-2">
        {reportTypes.map(type => (
          <button
            key={type.id}
            onClick={() => setReportType(type.id)}
            className={`flex items-center space-x-2 px-4 py-2 rounded-xl transition-colors ${
              reportType === type.id
                ? 'bg-blue-500 text-white'
                : 'bg-white/10 text-white/60 hover:bg-white/20'
            }`}
          >
            <type.icon className="h-4 w-4" />
            <span>{type.name}</span>
          </button>
        ))}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-4">
        <select
          value={dateRange}
          onChange={(e) => setDateRange(e.target.value)}
          className="bg-white/10 border border-white/20 rounded-xl px-4 py-2 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="this-week">Cette semaine</option>
          <option value="this-month">Ce mois</option>
          <option value="last-month">Mois dernier</option>
          <option value="this-quarter">Ce trimestre</option>
          <option value="this-year">Cette année</option>
          <option value="all-time">Toute la période</option>
        </select>

        <select
          value={selectedCategory}
          onChange={(e) => setSelectedCategory(e.target.value)}
          className="bg-white/10 border border-white/20 rounded-xl px-4 py-2 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="all">Toutes catégories</option>
          <option value="cardio">Cardio</option>
          <option value="strength">Musculation</option>
          <option value="functional">Fonctionnel</option>
          <option value="accessories">Accessoires</option>
        </select>

        <select
          value={selectedStatus}
          onChange={(e) => setSelectedStatus(e.target.value)}
          className="bg-white/10 border border-white/20 rounded-xl px-4 py-2 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="all">Tous les statuts</option>
          <option value="available">Disponible</option>
          <option value="maintenance">Maintenance</option>
          <option value="out-of-order">Hors service</option>
        </select>
      </div>

      {/* Report Content */}
      {reportType === 'overview' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Equipment by Category */}
          <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
            <h3 className="text-lg font-semibold text-white mb-4">Répartition par Catégorie</h3>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={categoryData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {categoryData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip
                    contentStyle={{
                      backgroundColor: 'rgba(255, 255, 255, 0.1)',
                      border: '1px solid rgba(255, 255, 255, 0.2)',
                      borderRadius: '12px',
                      color: 'white',
                    }}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Equipment Status */}
          <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
            <h3 className="text-lg font-semibold text-white mb-4">Statut des Équipements</h3>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={statusData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                  <XAxis dataKey="name" stroke="rgba(255,255,255,0.6)" />
                  <YAxis stroke="rgba(255,255,255,0.6)" />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: 'rgba(255, 255, 255, 0.1)',
                      border: '1px solid rgba(255, 255, 255, 0.2)',
                      borderRadius: '12px',
                      color: 'white',
                    }}
                  />
                  <Bar dataKey="value" fill={(entry) => entry.color} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Equipment Age Distribution */}
          <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
            <h3 className="text-lg font-semibold text-white mb-4">Distribution par Âge</h3>
            <div className="space-y-3">
              {ageDistributionData.map((item, index) => (
                <div key={index} className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div
                      className="w-4 h-4 rounded-full"
                      style={{ backgroundColor: item.color }}
                    />
                    <span className="text-white/80">{item.range}</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <span className="text-white font-medium">{item.count}</span>
                    <div className="w-16 bg-white/10 rounded-full h-2">
                      <div
                        className="h-2 rounded-full"
                        style={{
                          width: `${(item.count / Math.max(...ageDistributionData.map(d => d.count))) * 100}%`,
                          backgroundColor: item.color,
                        }}
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Recent Equipment */}
          <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
            <h3 className="text-lg font-semibold text-white mb-4">Équipements Récents</h3>
            <div className="space-y-3">
              {equipment
                .sort((a, b) => new Date(b.purchaseDate || 0) - new Date(a.purchaseDate || 0))
                .slice(0, 5)
                .map((item, index) => (
                  <div key={index} className="flex items-center justify-between p-3 bg-white/5 rounded-xl">
                    <div>
                      <h4 className="font-medium text-white">{item.name}</h4>
                      <p className="text-white/60 text-sm">{item.brand} - {item.model}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-white/80 text-sm">
                        {item.purchaseDate ? new Date(item.purchaseDate).toLocaleDateString('fr-FR') : 'N/A'}
                      </p>
                      <p className="text-white/60 text-xs">{formatCurrency(item.purchasePrice || 0)}</p>
                    </div>
                  </div>
                ))}
            </div>
          </div>
        </div>
      )}

      {reportType === 'maintenance' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Monthly Maintenance Cost */}
          <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20 lg:col-span-2">
            <h3 className="text-lg font-semibold text-white mb-4">Coûts de Maintenance Mensuels</h3>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={monthlyMaintenanceData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                  <XAxis dataKey="month" stroke="rgba(255,255,255,0.6)" />
                  <YAxis stroke="rgba(255,255,255,0.6)" />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: 'rgba(255, 255, 255, 0.1)',
                      border: '1px solid rgba(255, 255, 255, 0.2)',
                      borderRadius: '12px',
                      color: 'white',
                    }}
                    formatter={(value, name) => [
                      name === 'cost' ? formatCurrency(value) : value,
                      name === 'cost' ? 'Coût' : 'Tâches'
                    ]}
                  />
                  <Area
                    type="monotone"
                    dataKey="cost"
                    stroke="#3b82f6"
                    fill="url(#gradient)"
                    strokeWidth={2}
                  />
                  <defs>
                    <linearGradient id="gradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Top Maintenance Issues */}
          <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
            <h3 className="text-lg font-semibold text-white mb-4">Principaux Problèmes</h3>
            <div className="space-y-3">
              {maintenanceIssues.map((issue, index) => (
                <div key={index} className="flex items-center justify-between p-3 bg-white/5 rounded-xl">
                  <div>
                    <h4 className="font-medium text-white">{issue.issue}</h4>
                    <p className="text-white/60 text-sm">{issue.count} occurrences</p>
                  </div>
                  <div className="text-right">
                    <p className="text-white font-medium">{formatCurrency(issue.cost)}</p>
                    <p className="text-white/60 text-xs capitalize">{issue.category}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Maintenance Timeline */}
          <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
            <h3 className="text-lg font-semibold text-white mb-4">Chronologie des Maintenances</h3>
            <div className="space-y-3">
              {maintenance
                .sort((a, b) => new Date(b.scheduledDate || 0) - new Date(a.scheduledDate || 0))
                .slice(0, 6)
                .map((item, index) => (
                  <div key={index} className="flex items-center space-x-3">
                    <div className={`w-3 h-3 rounded-full ${
                      item.status === 'completed' ? 'bg-green-400' :
                      item.status === 'in-progress' ? 'bg-yellow-400' :
                      'bg-blue-400'
                    }`} />
                    <div className="flex-1">
                      <h4 className="font-medium text-white text-sm">{item.equipmentName}</h4>
                      <p className="text-white/60 text-xs">{item.description}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-white/80 text-xs">
                        {item.scheduledDate ? new Date(item.scheduledDate).toLocaleDateString('fr-FR') : 'N/A'}
                      </p>
                      <p className="text-white/60 text-xs">{formatCurrency(item.cost || 0)}</p>
                    </div>
                  </div>
                ))}
            </div>
          </div>
        </div>
      )}

      {reportType === 'utilization' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Equipment Utilization */}
          <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20 lg:col-span-2">
            <h3 className="text-lg font-semibold text-white mb-4">Taux d'Utilisation des Équipements</h3>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={utilizationData} layout="horizontal">
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                  <XAxis type="number" domain={[0, 100]} stroke="rgba(255,255,255,0.6)" />
                  <YAxis dataKey="name" type="category" stroke="rgba(255,255,255,0.6)" width={150} />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: 'rgba(255, 255, 255, 0.1)',
                      border: '1px solid rgba(255, 255, 255, 0.2)',
                      borderRadius: '12px',
                      color: 'white',
                    }}
                    formatter={(value) => [`${value}%`, 'Utilisation']}
                  />
                  <Bar dataKey="utilization" fill="#3b82f6" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Most Used Equipment */}
          <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
            <h3 className="text-lg font-semibold text-white mb-4">Équipements les Plus Utilisés</h3>
            <div className="space-y-3">
              {utilizationData
                .sort((a, b) => b.utilization - a.utilization)
                .slice(0, 5)
                .map((item, index) => (
                  <div key={index} className="flex items-center justify-between p-3 bg-white/5 rounded-xl">
                    <div>
                      <h4 className="font-medium text-white">{item.name}</h4>
                      <p className="text-white/60 text-sm capitalize">{item.category}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-white font-medium">{item.utilization}%</p>
                      <p className="text-white/60 text-xs">{item.hours}h/jour</p>
                    </div>
                  </div>
                ))}
            </div>
          </div>

          {/* Least Used Equipment */}
          <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
            <h3 className="text-lg font-semibold text-white mb-4">Équipements Sous-utilisés</h3>
            <div className="space-y-3">
              {utilizationData
                .sort((a, b) => a.utilization - b.utilization)
                .slice(0, 5)
                .map((item, index) => (
                  <div key={index} className="flex items-center justify-between p-3 bg-white/5 rounded-xl">
                    <div>
                      <h4 className="font-medium text-white">{item.name}</h4>
                      <p className="text-white/60 text-sm capitalize">{item.category}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-white font-medium">{item.utilization}%</p>
                      <p className="text-white/60 text-xs">{item.hours}h/jour</p>
                    </div>
                  </div>
                ))}
            </div>
          </div>
        </div>
      )}
    </motion.div>
  );
};

export default EquipmentReports; 